/**
 * Sender value for automated status notes written into a collab thread (e.g. "Jane has
 * rescheduled for Sat, Mar 8 at 7:00 PM EST").
 *
 * Deliberately NOT 'client' or 'influencer': a note is nobody's message. Attributing it to the
 * brand would render it as brand outreach (and text the creator a preview of a message about
 * their own action); attributing it to the creator would make it look like they typed it and
 * would count as creator activity for the check-in quiet-window. Both inboxes render this
 * sender as a centered pill and exclude it from unread counts.
 */
export const SYSTEM_SENDER = 'system';

/**
 * Which collab event a system note describes — lets callers filter notes without parsing `body`.
 *
 * Mostly creator actions, which the brand needs telling about. A brand-side change earns a kind
 * only when the CREATOR needs telling — the brand already knows what it just did — which is why
 * `deliverablesUpdated` is here but marking a collab complete is not (that is a plain message,
 * so it lands in the creator's unread count).
 */
export type SystemNoteKind =
  /** Visit date moved on an already-scheduled in-store collab. */
  | 'reschedule'
  /** Collab accepted — includes the first visit booking or posting date where there is one. */
  | 'accepted'
  /** Posting date changed after acceptance. */
  | 'postingDateChanged'
  /** Brand extended the content due date. */
  | 'contentDueDateChanged'
  /** Creator confirmed the visit happened and moved on to submitting content. */
  | 'visitConfirmed'
  /** Draft uploaded for brand approval. */
  | 'draftSubmitted'
  /** Live post link or finished UGC file submitted. */
  | 'contentSubmitted'
  /** Creator canceled a collab they had already accepted. */
  | 'canceled'
  /** Creator declined the invite before ever accepting it. */
  | 'declined'
  /** Brand changed what the creator owes — deliverables or UGC requirements edited. */
  | 'deliverablesUpdated'
  /** Brand sent the creator extra items on a Shopify collab, beyond the original order. */
  | 'extraShipment';

export const isSystemNote = (message?: { sender?: string } | null): boolean =>
  message?.sender === SYSTEM_SENDER;

/**
 * Copy for the kinds whose wording is FIXED, rendered from here rather than read back from the
 * stored `body`. A reword therefore reaches every historical note at once, and the creator and
 * brand inboxes cannot drift apart.
 *
 * Partial on purpose. A kind is absent when its note carries per-collab detail no map can hold —
 * `reschedule` says "Jane has rescheduled for Sat, Mar 8 at 7:00 PM EST" — and those render the
 * body captured at write time. Writers still store a body for every kind, so a reader that has
 * not been taught this map (or a kind added after it) shows something sensible.
 */
export const SYSTEM_NOTE_TEXT: Partial<Record<SystemNoteKind, string>> = {
  deliverablesUpdated: 'The deliverables & requirements of this collaboration have been adjusted.',
};

/** What a note should display: fixed copy where there is any, else the body stored at write time. */
export const systemNoteBody = (
  message?: { body?: string; systemNoteKind?: SystemNoteKind } | null,
): string =>
  (message?.systemNoteKind && SYSTEM_NOTE_TEXT[message.systemNoteKind]) || message?.body || '';

/** The two parties who can react to a message: the `sender` values of the brand and the creator. */
export type ReactionParty = 'client' | 'influencer';

/**
 * The one reaction either party can leave today: a red heart. It is stored on each reaction anyway,
 * so another can be added later without migrating the hearts already written.
 */
export const HEART_REACTION = '❤️';
export type ReactionEmoji = typeof HEART_REACTION;

/**
 * Reactions on a message, keyed by the party that left each — at most one per side, so toggling
 * is a set or delete of a single field. `T` is the Timestamp type of whichever SDK reads it.
 */
export type MessageReactions<T> = Partial<Record<ReactionParty, { emoji: ReactionEmoji; time: T }>>;

/**
 * Whether `party` may react to this message: only to the OTHER party's messages, and never to a
 * system note, which is nobody's message.
 */
export const canReactToMessage = (
  message: { sender?: string } | null | undefined,
  party: ReactionParty,
): boolean =>
  (message?.sender === 'client' || message?.sender === 'influencer') && message.sender !== party;

/** Whether `party` has reacted to this message. */
export const hasReactionFrom = (
  message: { reactions?: Partial<Record<ReactionParty, unknown>> } | null | undefined,
  party: ReactionParty,
): boolean => !!message?.reactions?.[party];
